import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  useIsFetching,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { NavResetContext } from './components/nav-reset.js';
import { NoticeProvider } from './components/notice.js';
import { Button, Loading } from './components/ui.js';
import { SignIn } from './features/auth/sign-in.js';
import { CustomersScreen } from './features/customers/customers-screen.js';
import { OnlineOrdersScreen } from './features/online/online-orders-screen.js';
import { HelpPanel } from './features/help/help-panel.js';
import type { HelpReader } from './features/help/pages.js';
import { FeedbackButton } from './features/feedback/feedback-button.js';
import { RegisterScreen } from './features/register/register-screen.js';
/*
 * Reports load when they are opened (2026-10-16). They are back-office work —
 * the grouped reports, the custom report builder, printing and spreadsheets —
 * and the register's first load has a budget (scripts/check-bundle-budget.mjs)
 * that custom reports took 2.9 KiB past. The register itself never waits on it.
 */
const ReportsScreen = lazy(() =>
  import('./features/reports/reports-screen.js').then((module) => ({
    default: module.ReportsScreen,
  })),
);
/*
 * Settings loads when its tab opens (decision 230): admin-only and opened
 * rarely, and card payments' three cards took the register's first load past
 * its budget (285.2 of 280 KiB). The register itself never waits on it.
 */
const SettingsScreen = lazy(() =>
  import('./features/settings/settings-screen.js').then((module) => ({
    default: module.SettingsScreen,
  })),
);
import { RepairsScreen } from './features/repairs/repairs-screen.js';
import { SalesScreen } from './features/sales/sales-screen.js';
import { StockSection } from './features/stock/stock-section.js';
import type { RegisterContext } from './features/register/register-screen.js';
import { HubFeedbackButton } from './features/feedback/hub-feedback.js';
import { api, errorMessage } from './lib/api.js';
import { installDiagnostics, recordBreadcrumb, recordError } from './lib/diagnostics.js';
import { feedbackProjectKey } from './lib/feedback-hub.js';
import type { SessionEmployee } from './lib/feedback-hub.js';
import { SHOP_ORIGIN } from './lib/shop-link.js';
import { pathOf, placeFromPath, tabOf } from './lib/place.js';
import type { Place, View } from './lib/place.js';
import { usePublishedTopbarHeight } from './lib/topbar-height.js';
import './app.css';

/**
 * One owner per signal. When the feedback hub is live its widget installs its
 * own error and network capture on mount, and `lib/diagnostics.ts` forwards
 * into the widget's trail — so installing this app's handlers as well would
 * record every window error twice. Keyless, the native feedback path still
 * needs them.
 */
if (feedbackProjectKey() === null) installDiagnostics();

const queryClient = new QueryClient({
  // Every failed call is remembered, so a problem report arrives with the
  // errors already attached instead of "it said something in red".
  queryCache: new QueryCache({
    onError: (error, query) =>
      recordError(`query ${String(query.queryKey)}: ${errorMessage(error)}`),
  }),
  mutationCache: new MutationCache({
    onError: (error) => recordError(`mutation: ${errorMessage(error)}`),
  }),
  defaultOptions: {
    queries: {
      // The register cannot afford a spinner on the critical path (SCOPE §2-5),
      // so cached data is shown while a refetch happens behind it. Anything
      // that must never be stale — the current cart — opts out explicitly.
      staleTime: 15_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ActivityBar />
      <NoticeProvider>
        <AuthGate />
      </NoticeProvider>
    </QueryClientProvider>
  );
}

/**
 * One line at the very top that moves whenever the app is talking to the server.
 *
 * Individual buttons show their own spinner, but plenty of waiting has no
 * button attached — a background refetch, a query fired by switching screens.
 * SCOPE §2.2-6 asks for acknowledgement within 100ms of *anything*; this is the
 * app-wide answer, and it costs no layout because it is fixed and 3px tall.
 */
function ActivityBar() {
  const busy = useIsFetching() + useIsMutating() > 0;
  if (!busy) return null;
  return <div className="activity-bar" role="presentation" />;
}

/**
 * Five sections, well inside the seven §2.2-2 allows.
 *
 * It was seven, which is the ceiling, and the next feature had nowhere to go.
 * **Products, Import and Receiving are one job** — what we own, what we ordered,
 * what arrived — so they became `stock` (see `StockSection`), which is what
 * bought the room for purchase-order creation, backorders and the receiving
 * document. Grouping was always the answer the moment a peer could not be
 * added; End of day folding into Reports had bought the last reprieve, and that
 * only worked because End of day genuinely *is* a report.
 *
 * `settings` is deliberately still not a tab. It is not the same *kind* of
 * thing: every tab is somewhere you go during a shift, and this is somewhere
 * one admin goes twice a year. It sits in the utility cluster beside Feedback
 * and Sign out, which is not a decision point — it is where you look for things
 * about *you and your store* rather than about this sale.
 */
// `View` lives in `lib/place.ts` beside the address of each one (FB-1122).

function AuthGate() {
  const session = useQuery({ queryKey: ['auth', 'me'], queryFn: () => api.auth.me.query() });
  /*
   * Where this person is, read from the address and written back to it
   * (FB-1122, decision 201): Back and Forward walk Shekko's own screens and a
   * reload keeps the place. An address that names nothing is the register —
   * home, where everything else is somewhere you go on purpose.
   */
  const [place, setPlace] = useState<Place>(() => placeFromPath(window.location.pathname));
  const requestedView = place.view;
  /** Go somewhere: a new entry in the browser's history, so Back returns here. */
  const go = useCallback((next: Place) => {
    const path = pathOf(next);
    if (path !== window.location.pathname) window.history.pushState(null, '', path);
    setPlace(next);
  }, []);
  useEffect(() => {
    // The address written the way this app writes it — `/inventory/` and
    // `/reports/end-of-day` both become what a tab press would produce.
    const written = pathOf(placeFromPath(window.location.pathname));
    if (written !== window.location.pathname) window.history.replaceState(null, '', written);
    const onPop = () => {
      const next = placeFromPath(window.location.pathname);
      recordBreadcrumb('navigate', `back/forward → ${pathOf(next)}`);
      setPlace(next);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [registerContext, setRegisterContext] = useState<RegisterContext>({
    registerId: null,
    saleId: null,
    saleNumber: null,
    registerName: null,
  });
  /** A customer handed over from Sales, to be opened on arrival. */
  const [openCustomerId, setOpenCustomerId] = useState<string | null>(null);
  /** Help, beside the screen (decision 160). */
  const [helpOpen, setHelpOpen] = useState(false);
  /*
   * What the store has, for the help to show only what applies here — dual
   * pricing, shipping. The same query every screen that reads settings uses.
   */
  const storeSettings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.settings.get.query(),
    enabled: session.data?.signedIn === true,
    staleTime: 5 * 60_000,
  });
  // Card payments through BlueSage (decision 230): their help is shown only to a store that has them.
  const cardMode = useQuery({
    queryKey: ['payments', 'registerMode'],
    queryFn: () => api.payments.registerMode.query(),
    enabled: session.data?.signedIn === true,
    staleTime: 30_000,
  });

  /**
   * Per-section reset counters (item 31): a re-press of the current tab bumps
   * its own counter and each section takes itself back to its start. Per
   * section, never global — re-pressing Stock must not reset the register
   * hidden behind it.
   */
  const [navResets, setNavResets] = useState<Partial<Record<View, number>>>({});
  const resetOf = (of: View) => navResets[of] ?? 0;

  const changeView = (next: View) => {
    const view = requestedView;
    if (next === view) {
      // The tab's name promises a place; pressing it takes you there even —
      // especially — when you are already in the section, drilled into
      // something (item 31).
      recordBreadcrumb('navigate', `${next} → its start`);
      setNavResets((current) => ({ ...current, [next]: (current[next] ?? 0) + 1 }));
      return;
    }
    recordBreadcrumb('navigate', `${view} → ${next}`);
    // Dropped on the way anywhere else, so returning to Customers by the tab
    // does not re-open whoever was last looked up from a receipt.
    if (next !== 'customers') setOpenCustomerId(null);
    go({ view: next, tab: null });
  };

  /**
   * Open Reports on a named report (decision 122): how the low-stock banner
   * in Stock lands on the Reorder list. Cleared once Reports has taken it, so
   * pressing Reports later opens where it always does.
   */
  const openReport = (tab: string) => {
    recordBreadcrumb('navigate', `${requestedView} → reports (${tab})`);
    go({ view: 'reports', tab });
  };

  const openCustomer = (customerId: string) => {
    setOpenCustomerId(customerId);
    recordBreadcrumb('navigate', `${requestedView} → customers`);
    go({ view: 'customers', tab: null });
  };

  const onRegisterContext = useCallback((context: RegisterContext) => {
    setRegisterContext((previous) =>
      previous.registerId === context.registerId &&
      previous.saleId === context.saleId &&
      previous.saleNumber === context.saleNumber &&
      previous.registerName === context.registerName
        ? previous
        : context,
    );
  }, []);

  if (session.isPending) {
    return <Loading label="Starting" />;
  }

  if (session.isError || session.data?.signedIn !== true) {
    // `auth.me` answers with the tenant even when signed out, so the sign-in
    // screen can name the store rather than the product (§12.3).
    return <SignIn storeName={session.data?.tenant?.name ?? null} />;
  }

  // Read out of the narrowed value, not through `session.data` — the guard
  // above does not survive into a closure.
  const granted = session.data.permissions;
  const has = (permission: string) => granted.includes(permission);

  /** Writing an order, or importing one: a buying decision, so manager and up. */
  const canPurchase = has('purchasing.create');
  const canReconcile = has('purchasing.reconcile');
  /**
   * Receiving is stockroom work, not a management decision (§6.1), and an
   * associate is granted `receiving.receive`. The nav never offered it to them
   * — Receiving was gated on `purchasing.create` — so the server said yes and
   * the screen was unreachable. Gated on the permission that actually governs
   * it now.
   */
  const canReceive = has('receiving.receive');
  // Everyone can look a product up — an associate at the counter has to be able
  // to say what a thing costs. Only a buyer can change it (§5.2).
  const canManageCatalog = has('catalog.manage');
  const canManageSettings = has('settings.manage');
  // A rate is money, so it is its own permission rather than folded into
  // settings — same reasoning as `customer.tax_exempt` (decision 33).
  const canManageTax = has('tax.manage');
  const canManageUsers = has('users.manage');
  /** What the shop paid is buying information, so the reports stop at manager. */
  const canViewAllReports = has('report.all');
  // End of day is everyone's (decision 202); the rest stops at manager.
  const canViewReports = canViewAllReports || has('report.end_of_day');
  /** Working the web shop's orders is counter work: anyone who can ring a sale. */
  const canWorkOnlineOrders = has('sale.create');

  /*
   * An address this person may not open — Settings for an associate, a link
   * someone else shared — shows the register, never an empty screen.
   */
  const permitted: Record<View, boolean> = {
    register: true,
    sales: true,
    online: canWorkOnlineOrders,
    customers: true,
    repairs: true,
    stock: true,
    reports: canViewReports,
    settings: canManageSettings,
  };
  const view: View = permitted[requestedView] ? requestedView : 'register';

  const helpReader: HelpReader = {
    permissions: new Set(granted),
    capabilities: new Set(
      [
        storeSettings.data?.dualPricing.enabled === true ? 'dual-pricing' : null,
        storeSettings.data?.onlineShop.shipping.enabled === true ? 'shipping' : null,
        cardMode.data?.enabled === true ? 'card-terminals' : null,
      ].filter((capability): capability is string => capability !== null),
    ),
  };

  return (
    <>
      <TopBar
        onHelp={() => setHelpOpen((open) => !open)}
        helpOpen={helpOpen}
        storeName={session.data.tenant?.name ?? 'Shekko'}
        employee={session.data.employee}
        view={view}
        canViewReports={canViewReports}
        canWorkOnlineOrders={canWorkOnlineOrders}
        canManageSettings={canManageSettings}
        onChangeView={changeView}
        feedbackContext={{
          view,
          registerId: registerContext.registerId,
          saleId: registerContext.saleId,
          saleNumber: registerContext.saleNumber,
          registerName: registerContext.registerName,
        }}
      />
      {helpOpen ? (
        <HelpPanel
          reader={helpReader}
          currentScreen={view}
          onTakeMeThere={(screen, tab) => {
            // The tab the task is on, not only the section (FB-1083).
            // Already in the section, only the tab moves: pressing the section
            // again would reset it to its start and undo the tab (FB-1100).
            if (tab !== null && (screen === 'stock' || screen === 'reports')) {
              go({ view: screen, tab });
            } else if (screen !== view) changeView(screen);
          }}
          onClose={() => setHelpOpen(false)}
        />
      ) : null}
      <div className={helpOpen ? 'app-main app-main--help' : 'app-main'}>
        {/*
        The register stays mounted, so switching sections is instant and it
        never loses its place. Its cart lives on the server regardless; Stock
        keeps its own screens mounted for the same reason, one level down.
      */}
        <div hidden={view !== 'register'}>
          <RegisterScreen onContext={onRegisterContext} canSetUpTills={canManageSettings} />
        </div>
        {/* Mounted only when open: these are lookups, not places you keep a cart. */}
        {view === 'sales' ? (
          // Starting a return hands the refund cart to the register, which is
          // where money is taken — so the operator lands where they need to be.
          <NavResetContext.Provider value={resetOf('sales')}>
            <SalesScreen
              onReturnStarted={() => changeView('register')}
              onOpenCustomer={openCustomer}
              {...(canWorkOnlineOrders ? { onOpenOnline: () => changeView('online') } : {})}
            />
          </NavResetContext.Provider>
        ) : null}
        {/*
        Online orders (decision 156), reached from Sales (decision 159): the web
        shop's paid orders, as work. Its own place, so a picker can keep it open.
      */}
        {view === 'online' && canWorkOnlineOrders ? (
          <NavResetContext.Provider value={resetOf('online')}>
            <OnlineOrdersScreen
              registerId={registerContext.registerId}
              onReturnStarted={() => changeView('register')}
              onBackToSales={() => changeView('sales')}
            />
          </NavResetContext.Provider>
        ) : null}
        {/*
        A return can start here too. "I bought this here last month" is answered
        by finding the person, then the item, then the receipt it was on — and
        the return that follows lands at the register like any other.
      */}
        {view === 'customers' ? (
          <NavResetContext.Provider value={resetOf('customers')}>
            <CustomersScreen
              onReturnStarted={() => changeView('register')}
              initialCustomerId={openCustomerId}
            />
          </NavResetContext.Provider>
        ) : null}
        {/*
        Repairs (decision 127). Ringing a ticket up puts it in this device's cart
        and lands the operator at the register, where the money is taken.
      */}
        {view === 'repairs' ? (
          <NavResetContext.Provider value={resetOf('repairs')}>
            <RepairsScreen onRungUp={() => changeView('register')} />
          </NavResetContext.Provider>
        ) : null}
        {view === 'stock' ? (
          <NavResetContext.Provider value={resetOf('stock')}>
            <StockSection
              tab={tabOf(place, 'stock')}
              onTab={(tab) => go({ view: 'stock', tab })}
              canManageCatalog={canManageCatalog}
              canPurchase={canPurchase}
              canReconcile={canReconcile}
              canReceive={canReceive}
              {...(canViewAllReports ? { onOpenReport: openReport } : {})}
            />
          </NavResetContext.Provider>
        ) : null}
        {view === 'reports' && canViewReports ? (
          <NavResetContext.Provider value={resetOf('reports')}>
            <Suspense fallback={<Loading label="Opening Reports" />}>
              <ReportsScreen
                canViewAll={canViewAllReports}
                tab={tabOf(place, 'reports')}
                onTab={(tab) => go({ view: 'reports', tab })}
              />
            </Suspense>
          </NavResetContext.Provider>
        ) : null}
        {view === 'settings' && canManageSettings ? (
          <Suspense fallback={<Loading label="Opening Settings" />}>
            <SettingsScreen
              canManageTax={canManageTax}
              canManageUsers={canManageUsers}
              tab={tabOf(place, 'settings')}
              onTab={(tab) => go({ view: 'settings', tab })}
            />
          </Suspense>
        ) : null}
      </div>
    </>
  );
}

/**
 * Context, always in the same place (SCOPE §2.2-4).
 *
 * Who is signed in is permanently visible, because every action is attributed
 * to them and a register shared by six people cannot leave that ambiguous.
 */
function TopBar({
  onHelp,
  helpOpen,
  storeName,
  employee,
  view,
  canViewReports,
  canWorkOnlineOrders,
  canManageSettings,
  onChangeView,
  feedbackContext,
}: {
  onHelp: () => void;
  helpOpen: boolean;
  storeName: string;
  employee: SessionEmployee & { displayName: string };
  view: View;
  canViewReports: boolean;
  canWorkOnlineOrders: boolean;
  canManageSettings: boolean;
  onChangeView: (view: View) => void;
  feedbackContext: {
    view: string;
    registerId: string | null;
    saleId: string | null;
    saleNumber: string | null;
    registerName: string | null;
  };
}) {
  const queryClient = useQueryClient();

  const signOut = useMutation({
    mutationFn: () => api.auth.logout.mutate(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['auth', 'me'] }),
  });

  /*
   * The register pins its own frame against this bar's height (decision R3),
   * and that height cannot be written down — the bar wraps on a narrow screen.
   * So it publishes its own measurement. See `usePublishedTopbarHeight`.
   */
  const bar = useRef<HTMLDivElement>(null);
  usePublishedTopbarHeight(bar);

  /*
   * The work waiting on the web shop's orders (S5-8). Polled once a minute —
   * the only thing in the bar that changes without anybody here doing
   * anything, because a shopper somewhere else just paid.
   */
  const onlineWaiting = useQuery({
    queryKey: ['onlineOrders', 'count'],
    queryFn: () => api.onlineOrders.count.query(),
    enabled: canWorkOnlineOrders,
    refetchInterval: 60_000,
  });
  const waiting = onlineWaiting.data ?? 0;

  /*
   * On a phone the tabs are a strip you swipe (see app.css), so the one you
   * are on could be scrolled out of sight. Bring it into view on every change —
   * "nearest", so on a wide screen, where every tab already shows, nothing moves.
   */
  useEffect(() => {
    bar.current
      ?.querySelector('.topbar__tab--active')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [view]);

  return (
    <div className="topbar" ref={bar}>
      <div className="topbar__inner">
        {/*
          The store name is the way home, and home is the register.

          Every web application has taught people that the name in the top-left
          goes back to the start, so leaving it inert spends a convention the
          product gets for free — and the register is where a counter should
          land from anywhere, mid-shift, without hunting for the tab.
        */}
        <button
          type="button"
          className="topbar__store"
          onClick={() => onChangeView('register')}
          aria-label={`${storeName} — back to the register`}
        >
          {storeName}
        </button>

        <nav className="topbar__nav" aria-label="Sections">
          <button
            type="button"
            className={`topbar__tab${view === 'register' ? ' topbar__tab--active' : ''}`}
            onClick={() => onChangeView('register')}
            aria-current={view === 'register' ? 'page' : undefined}
          >
            Register
          </button>
          {/* Only shown to staff who can actually use it — a visible control
              that always refuses is worse than no control (§2.2-7). */}
          {/*
            Sales holds the online orders too (decision 159): a seventh tab made
            the bar scroll on an ordinary screen. The count stays on the bar —
            online orders waiting on staff — so it is seen from anywhere, and it
            is part of the name a screen reader hears: "Sales, 2 online orders
            to do".
          */}
          <button
            type="button"
            className={`topbar__tab${view === 'sales' || view === 'online' ? ' topbar__tab--active' : ''}`}
            onClick={() => onChangeView('sales')}
            aria-current={view === 'sales' || view === 'online' ? 'page' : undefined}
            aria-label={
              waiting > 0
                ? `Sales, ${waiting} online order${waiting === 1 ? '' : 's'} to do`
                : 'Sales'
            }
          >
            Sales
            {waiting > 0 ? (
              <span className="topbar__count" aria-hidden="true">
                {waiting}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            className={`topbar__tab${view === 'customers' ? ' topbar__tab--active' : ''}`}
            onClick={() => onChangeView('customers')}
            aria-current={view === 'customers' ? 'page' : undefined}
          >
            Customers
          </button>
          {/*
            Repairs gets a tab of its own (decision 127): in the tune-up season
            it is a daily queue a technician works from, not a lookup. Six of
            the seven choices §2.2-2 allows.
          */}
          <button
            type="button"
            className={`topbar__tab${view === 'repairs' ? ' topbar__tab--active' : ''}`}
            onClick={() => onChangeView('repairs')}
            aria-current={view === 'repairs' ? 'page' : undefined}
          >
            Repairs
          </button>
          {/*
            One section, not three. Products, Import and Receiving are all the
            same job — what we own, what we ordered, what arrived — and keeping
            them apart is what put this bar at the seven-choice ceiling §2.2-2
            sets with more still to build. Five now, for everyone.
          */}
          <button
            type="button"
            className={`topbar__tab${view === 'stock' ? ' topbar__tab--active' : ''}`}
            onClick={() => onChangeView('stock')}
            aria-current={view === 'stock' ? 'page' : undefined}
          >
            Inventory
          </button>
          {/*
            Everyone holds `report.end_of_day` and gets End of day for any
            drawer (decision 202); `report.all` stops at manager (§18.1-4).
            Hidden outright for anyone with neither, because a tab that always
            refuses is worse than no tab (§2.2-7).
          */}
          {canViewReports ? (
            <button
              type="button"
              className={`topbar__tab${view === 'reports' ? ' topbar__tab--active' : ''}`}
              onClick={() => onChangeView('reports')}
              aria-current={view === 'reports' ? 'page' : undefined}
            >
              Reports
            </button>
          ) : null}
        </nav>

        <div className="topbar__right">
          {/*
            Always visible, never in the way. It lives here rather than floating
            in a screen corner because the register pins its primary action
            bottom-right and Cancel Sale bottom-left (§2.2-1) — a floating
            button in either corner would sit next to one of them.
          */}
          {/* How to do anything here, beside the screen (decision 160). */}
          <Button
            variant={helpOpen ? 'secondary' : 'ghost'}
            onClick={onHelp}
            aria-expanded={helpOpen}
            aria-label="Help"
            title="Help — how to do anything here"
            className="topbar__help"
          >
            <span aria-hidden="true">?</span>
          </Button>
          {feedbackProjectKey() === null ? (
            /*
             * The native path, until the hub key is issued. Feedback must never
             * go dark during the transition: the day the key lands in the
             * environment, the hub widget takes over and this branch stops
             * rendering — and once the cutover is verified against the hub,
             * the native path gets deleted rather than kept as a second door.
             */
            <FeedbackButton context={feedbackContext} />
          ) : (
            <HubFeedbackButton employee={employee} context={feedbackContext} />
          )}
          <span className="text-secondary topbar__who">{employee.displayName}</span>
          {/*
            The store's own web shop, as a customer sees it (decision 157) — in
            a new tab, so the register behind it keeps its place.
          */}
          <a
            className="button button--ghost button--medium topbar__shop"
            href={SHOP_ORIGIN}
            target="_blank"
            rel="noopener"
          >
            Web shop <span aria-hidden="true">↗</span>
          </a>
          {/*
            Not a tab (see `View`): a decision point is capped at seven and the
            nav is already there. Shown only to an admin, because a control that
            always refuses is worse than no control (§2.2-7).
          */}
          {canManageSettings ? (
            <span className="topbar__settings">
              <Button
                variant={view === 'settings' ? 'secondary' : 'ghost'}
                onClick={() => onChangeView(view === 'settings' ? 'register' : 'settings')}
                aria-current={view === 'settings' ? 'page' : undefined}
                aria-label={view === 'settings' ? 'Back to register' : 'Settings'}
              >
                {view === 'settings' ? (
                  '← Back to register'
                ) : (
                  <>
                    <span aria-hidden="true">⚙</span>
                    <span className="topbar__label"> Settings</span>
                  </>
                )}
              </Button>
            </span>
          ) : null}
          <Button variant="ghost" onClick={() => signOut.mutate()} busy={signOut.isPending}>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
